import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, UserPlus, Trash2, ShieldCheck } from "lucide-react";

const ROLES = [
  { v: "admin", label: "Owner / Administrator" },
  { v: "editor", label: "Editor" },
  { v: "ceo", label: "CEO / Executive Viewer" },
  { v: "reviewer", label: "Private Reviewer" },
  { v: "guest", label: "Guest" },
];

export default function Users() {
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "editor" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/users", form); toast.success("User created"); setForm({ name: "", email: "", password: "", role: "editor" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete this user?")) return; try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };
  const changeRole = async (id, role) => { try { await api.patch(`/users/${id}`, { role }); toast.success("Role updated"); load(); } catch { toast.error("Failed"); } };

  if (!users) return <div className="flex justify-center h-96 items-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-white tracking-tight">Users & Roles</h1>
        <p className="text-slate-400 mt-2">Role-based access control. Viewers only ever see published content.</p>
      </div>

      <form onSubmit={create} className="rounded-xl border border-white/5 bg-[#111827] p-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <div><label className="text-xs text-slate-500">Name</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" className="w-full bg-[#070B14] border border-white/10 rounded-lg p-2 text-sm text-white mt-1 outline-none focus:border-blue-500" /></div>
        <div><label className="text-xs text-slate-500">Email</label><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" className="w-full bg-[#070B14] border border-white/10 rounded-lg p-2 text-sm text-white mt-1 outline-none focus:border-blue-500" /></div>
        <div><label className="text-xs text-slate-500">Password</label><input required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" className="w-full bg-[#070B14] border border-white/10 rounded-lg p-2 text-sm text-white mt-1 outline-none focus:border-blue-500" /></div>
        <div><label className="text-xs text-slate-500">Role</label><select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role" className="w-full bg-[#070B14] border border-white/10 rounded-lg p-2 text-sm text-white mt-1 outline-none focus:border-blue-500">{ROLES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}</select></div>
        <button type="submit" disabled={busy} data-testid="create-user-btn" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg px-4 py-2 transition-colors">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Add</button>
      </form>

      <div className="rounded-xl border border-white/5 bg-[#111827] overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/5 text-slate-500 text-xs uppercase tracking-wider"><th className="text-left p-4">User</th><th className="text-left p-4">Role</th><th className="p-4"></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-white/5 last:border-0">
                <td className="p-4"><div className="text-white">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></td>
                <td className="p-4">
                  <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)} className="bg-[#070B14] border border-white/10 rounded-lg p-1.5 text-xs text-white outline-none focus:border-blue-500">
                    {ROLES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                  </select>
                </td>
                <td className="p-4 text-right"><button onClick={() => del(u.id)} data-testid={`delete-user-${u.id}`} className="text-slate-500 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
